const formatDate = (date: Date): string =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;

const getMonthBounds = (date: Date): { startDate: Date; endDate: Date } => {
  const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
  const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  return { startDate, endDate };
};

export const getCurrentMonthDateRange = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const { startDate, endDate } = getMonthBounds(today);
  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
};

export const getMonthDateRange = (date: Date): { startDate: string; endDate: string } => {
  const { startDate, endDate } = getMonthBounds(date);
  return { startDate: formatDate(startDate), endDate: formatDate(endDate) };
};

const MONTH_NAMES = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

export const getLast12MonthsLabels = (): string[] => {
  const months: string[] = [];
  const now = new Date();

  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(MONTH_NAMES[date.getMonth()]);
  }

  return months;
};

export const normalizeLast12MonthsData = <T extends { month: string }>(
  data: T[],
  defaultValues: Omit<T, "month">
): T[] => {
  const now = new Date();
  const last12Months: T[] = [];

  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthKey = MONTH_NAMES[date.getMonth()];

    const existingData = data.find((item) => {
      const itemMonth = item.month.toUpperCase().substring(0, 3);
      return itemMonth === monthKey;
    });

    if (existingData) {
      last12Months.push({ ...existingData, month: monthKey } as T);
    } else {
      last12Months.push({ month: monthKey, ...defaultValues } as T);
    }
  }

  return last12Months;
};

export function formatDateRange(startDate: Date, endDate: Date): string {
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const startMonth = startDate.getMonth();
  const endMonth = endDate.getMonth();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  const daysInMonth = new Date(endYear, endMonth + 1, 0).getDate();
  const sameMonth = startMonth === endMonth && startYear === endYear;
  const isStartOfMonth = startDay === 1;
  const isEndOfMonth = endDay === daysInMonth;

  if (sameMonth && isStartOfMonth && isEndOfMonth) {
    const formatter = new Intl.DateTimeFormat("pt-BR", {
      month: "long",
      year: "numeric",
    });

    const formatted = formatter.format(startDate);
    const monthName = formatted.split(" ")[0];
    const capitalizedMonth = monthName.charAt(0) + monthName.slice(1);

    return `${capitalizedMonth.toUpperCase()} ${startYear}`;
  }

  const formatDay = (d: Date) => d.toLocaleDateString("pt-BR", { timeZone: "UTC" });

  return `Período: de ${formatDay(startDate)} até ${formatDay(endDate)}`;
}
